import { useEffect, useMemo, useState } from 'react';

// Define public routes in a central place
export const PUBLIC_ROUTES = [
  '/', // home page
  '/blog',
  '/help',
  '/search',
  '/terms',
  '/privacy',
  '/itineraries', // public list of PUBLIC itineraries (no login)
  '/itineraries/create',
  '/profile', // public but shows sign-in prompt when not authenticated
] as string[];

// Type for matching route patterns
type RoutePattern = (path: string) => boolean;

// Define patterns for public routes (e.g., wildcards)
const PUBLIC_PATTERNS: RoutePattern[] = [
  // Example: Match all paths under /public/*
  //   (path: string) => path.startsWith('/public/'),
  // Match /itineraries/{id} but not /itineraries/{id}/edit or other sub-paths
  (path: string) => {
    const itineraryPattern = /^\/itineraries\/[^\/]+\/?$/;
    return itineraryPattern.test(path);
  },
  // Match /itineraries/{itineraryId}/stops/{stopId} for public stop viewing
  (path: string) => {
    const stopPattern = /^\/itineraries\/[^\/]+\/stops\/[^\/]+\/?$/;
    return stopPattern.test(path);
  },
  // Match /itineraries/reels/{id} for public reel viewing
  (path: string) => {
    const reelPattern = /^\/itineraries\/reels\/[^\/]+\/?$/;
    return reelPattern.test(path);
  },
  (path: string) => {
    const publicProfilePattern = /^\/users\/[^\/]+\/?$/;
    return publicProfilePattern.test(path);
  },
  // Blog articles stay reachable without login. The index route is an exact match.
  (path: string) => path === '/blog' || path.startsWith('/blog/'),
];

export function normalizeRoutePath(path: string): string {
  const withoutQuery = (path || '/').split('?')[0].split('#')[0];
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.replace(/\/+$/, '') || '/';
  }
  return withoutQuery || '/';
}

export function isPublicPath(path: string): boolean {
  const currentPath = normalizeRoutePath(path);

  if (PUBLIC_ROUTES.includes(currentPath)) {
    return true;
  }

  return PUBLIC_PATTERNS.some((pattern) => pattern(currentPath));
}

export type AuthGuardView = 'children' | 'loading' | 'sign-in';

/** Protected routes show a sign-in prompt instead of a blank page. */
export function getAuthGuardView(state: {
  requiresAuth: boolean;
  isAuthenticated: boolean;
  silentAuthAttempted: boolean;
  isInProgress: boolean;
}): AuthGuardView {
  if (!state.requiresAuth || state.isAuthenticated) {
    return 'children';
  }
  if (!state.silentAuthAttempted || state.isInProgress) {
    return 'loading';
  }
  return 'sign-in';
}

export const useRouteAuth = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);

    // Create a MutationObserver to watch for URL changes that don't trigger popstate
    const observer = new MutationObserver(() => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    });

    // Observe URL changes
    observer.observe(document, { subtree: true, childList: true });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [currentPath]);

  const isPublicRoute = useMemo((): boolean => {
    console.debug('Checking auth for path:', currentPath);
    return isPublicPath(currentPath);
  }, [currentPath]);

  return {
    requiresAuth: !isPublicRoute,
    isPublicRoute,
  };
};
